import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * The homepage "Product Solutions" products, read from the database.
 *
 * Admin → Home Page → Product Solutions stores only references ({ productId, order, status, label? }); this hook fetches
 * those products from the Products API in ONE request and returns them in the saved order. Name, image, description and
 * category therefore always come from the product record itself — edit a product in Admin → Products and this section
 * follows, with no second copy to keep in sync. Draft or deleted products are simply left out (the API serves published
 * products only).
 *
 * When the homepage has no selection yet, it falls back to products flagged "Featured on homepage" in Admin → Products.
 *
 * @returns {{ loading: boolean, entries: Array<{ product: object, label?: string }> }}
 */
export default function useFeaturedProducts(items) {
  const visible = (Array.isArray(items) ? items : [])
    .filter((i) => i && i.productId && i.status !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const key = visible.map((i) => i.productId).join(",");
  const [state, setState] = useState({ key: null, byId: null, fallback: [] });

  useEffect(() => {
    let alive = true;
    const request = key ? api.get("/products", { params: { ids: key, limit: 100 } }) : api.get("/products", { params: { featured: true, limit: 8 } });
    request
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setState({ key, byId: new Map(list.map((p) => [p.id, p])), fallback: list });
      })
      .catch(() => { if (alive) setState({ key, byId: new Map(), fallback: [] }); });
    return () => { alive = false; };
  }, [key]);

  if (state.key !== key || !state.byId) return { loading: true, entries: [] };
  const entries = key
    ? visible.map((i) => (state.byId.get(i.productId) ? { product: state.byId.get(i.productId), label: i.label } : null)).filter(Boolean)
    : state.fallback.map((product) => ({ product }));
  return { loading: false, entries };
}
